import { useState } from "react";
import * as Yup from "yup";
import intl from "react-intl-universal";
import { formatInputValue } from "../../../Utils/formatInputValue";
import { validateCPF_CNPJ } from "../../../Utils/CPF_CNPJValidation";
import { cleanTaxIDMask, handleTaxIDInput } from "../../../Utils/masks";
import { stringToDecimal, convertToCents } from "../../../Utils/formatters";
import { loadItem } from "../../../Utils";
import { SelectedAccountBalance } from "../PixTransferSteps/SelectedAccountBalance";

export const INTERNAL_TRANSFER_SEARCH_FORM_ID = "internal-transfer-search-form";
export const INTERNAL_TRANSFER_FORM_ID = "internal-transfer-form";

const VALUT_INSTITUTION_CODE = "000";

const formatInstitutionLabel = (bankTitle) => {
  const name = (bankTitle || "Valut").trim() || "Valut";
  return `${name} (${VALUT_INSTITUTION_CODE})`;
};

const RecipientSummary = ({ contactData, title }) => (
  <div className="pix-transfer-recipient pix-transfer-recipient--compact">
    <div className="pix-transfer-recipient__row">
      <span className="pix-transfer-recipient__label">Favorecido</span>
      <span className="pix-transfer-recipient__value">{contactData.name}</span>
    </div>
    <div className="pix-transfer-recipient__row">
      <span className="pix-transfer-recipient__label">Instituição</span>
      <span className="pix-transfer-recipient__value">{formatInstitutionLabel(title)}</span>
    </div>
  </div>
);

export const InternalDestinationStep = ({
  taxId,
  onTaxIdChange,
  onSearch,
  searchFormId = INTERNAL_TRANSFER_SEARCH_FORM_ID,
  showNotFound = false,
  notFoundTitle,
  notFoundMessage,
  hideFooter = false,
}) => {
  const { title } = loadItem("Options");
  const [documentError, setDocumentError] = useState("");

  const handleSubmit = async (event) => {
    event?.preventDefault?.();
    const document = cleanTaxIDMask(taxId);
    setDocumentError("");
    try {
      const isValid = validateCPF_CNPJ(document);
      const schema = Yup.object().shape({
        username: Yup.string()
          .required("CPF/CNPJ obrigatório.")
          .test("valid", "CPF/CNPJ inválido", () => isValid === true),
      });
      await schema.validate({ username: document }, { abortEarly: false });
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        setDocumentError(err.errors?.[0] || "CPF/CNPJ inválido");
      }
      return;
    }
    onSearch(document);
  };

  return (
    <section className="pix-transfer-section pix-transfer-section--compact app-card">
      <h3 className="pix-transfer-section__title">Destinatário</h3>
      <p className="pix-transfer-section__subtitle">
        Informe o CPF ou CNPJ da conta {title} de destino.
      </p>

      <form
        id={searchFormId}
        onSubmit={handleSubmit}
        className="mt-2"
        noValidate
      >
        <div>
          <label
            className="mb-1 block text-sm font-medium text-gray-500"
            htmlFor="internal-transfer-tax-id"
          >
            CPF ou CNPJ
          </label>
          <input
            id="internal-transfer-tax-id"
            name="username"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            aria-label="CPF ou CNPJ do destinatário"
            aria-invalid={Boolean(documentError)}
            value={taxId}
            placeholder="Digite o CPF ou CNPJ"
            onChange={(e) => {
              setDocumentError("");
              onTaxIdChange(handleTaxIDInput(e.currentTarget.value));
            }}
            className={`valut-input h-11 w-full rounded-xl placeholder:text-gray-400 ${
              documentError ? "!border-red-300 focus:!border-red-400" : ""
            }`}
          />
          {documentError && (
            <p className="mt-1.5 text-xs text-red-600" role="alert">
              {documentError}
            </p>
          )}
        </div>

        {!hideFooter && (
          <div className="mt-3 flex flex-col-reverse gap-2 border-t border-gray-100 pt-3 sm:flex-row sm:justify-end">
            <button type="submit" className="btn-primary">
              Buscar conta
            </button>
          </div>
        )}
      </form>

      {showNotFound && (
        <div
          role="alert"
          className="mt-2.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
        >
          <p className="font-semibold">
            {notFoundTitle || "Conta não encontrada"}
          </p>
          <p className="mt-1 text-red-700/90">
            {notFoundMessage ||
              `Nenhuma conta ${title} foi localizada para este documento. Verifique o CPF/CNPJ e tente novamente.`}
          </p>
        </div>
      )}
    </section>
  );
};

export const InternalTransferInfoStep = ({
  contactData,
  linkValue,
  onLinkValueChange,
  onContinue,
  transferFormId = INTERNAL_TRANSFER_FORM_ID,
  hideFooter = false,
  isSelfTransfer = false,
  selfTransferMessage =
    "Não é possível transferir para a mesma conta de origem. Selecione outra conta Valut.",
  sourceAccountBalance,
  sourceAccountBalanceCents,
  sourceAccountBalanceLoading = false,
  sourceAccountBalanceError = false,
  sourceAccountShowLimit = false,
  sourceAccountFormattedAvailable,
  sourceAccountFormattedLimit,
}) => {
  const { title } = loadItem("Options");
  const [valueError, setValueError] = useState("");
  const [description, setDescription] = useState("");
  const amountCents = convertToCents(linkValue || "0");
  const insufficientBalance =
    !sourceAccountBalanceLoading
    && !sourceAccountBalanceError
    && sourceAccountBalanceCents != null
    && amountCents > 0
    && amountCents > sourceAccountBalanceCents;

  const handleSubmit = async (event) => {
    event?.preventDefault?.();
    if (isSelfTransfer) return;
    const decimalValue = stringToDecimal(linkValue);
    setValueError("");
    try {
      const schema = Yup.object().shape({
        value: Yup.number()
          .typeError("Digite o valor da transferência")
          .required("Digite o valor da transferência")
          .moreThan(0, "Valor deve ser maior que R$ 0,00"),
      });
      await schema.validate({ value: decimalValue }, { abortEarly: false });
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        setValueError(err.errors?.[0] || "Digite o valor da transferência");
      }
      return;
    }
    const cents = convertToCents(linkValue);
    if (sourceAccountBalanceLoading || sourceAccountBalanceError || sourceAccountBalanceCents == null) {
      return;
    }
    if (cents > sourceAccountBalanceCents) {
      return;
    }
    onContinue({
      value: linkValue,
      description: description || "",
    });
  };

  return (
    <section className="pix-transfer-section pix-transfer-section--compact app-card">
      <h3 className="pix-transfer-section__title">Valor da transferência</h3>
      <p className="pix-transfer-section__subtitle">
        Informe o valor e, se desejar, uma descrição para a transferência.
      </p>

      <div className="mt-2">
        <RecipientSummary contactData={contactData} title={title} />
      </div>

      {sourceAccountBalance != null && (
        <div className="mt-3">
          <SelectedAccountBalance
            formattedBalance={sourceAccountBalance}
            loading={sourceAccountBalanceLoading}
            showAccountName={false}
            showLimit={sourceAccountShowLimit}
            formattedAvailable={sourceAccountFormattedAvailable}
            formattedLimit={sourceAccountFormattedLimit}
          />
        </div>
      )}

      {isSelfTransfer && (
        <div
          role="alert"
          className="mt-2.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
        >
          <p className="font-semibold">Transferência não permitida</p>
          <p className="mt-1 text-red-700/90">{selfTransferMessage}</p>
        </div>
      )}

      <form
        id={transferFormId}
        onSubmit={handleSubmit}
        className="mt-3 space-y-2.5"
        noValidate
      >
        <div>
          <label
            className="mb-1.5 block text-sm font-medium text-gray-500"
            htmlFor="internal-transfer-value"
          >
            Valor (R$)
          </label>
          <input
            id="internal-transfer-value"
            aria-label="Valor da transferência"
            aria-invalid={Boolean(valueError)}
            value={linkValue}
            onChange={(e) => {
              setValueError("");
              onLinkValueChange(formatInputValue(e.currentTarget.value));
            }}
            name="value"
            type="text"
            inputMode="decimal"
            maxLength={10}
            placeholder="0,00"
            disabled={isSelfTransfer}
            className={`valut-input h-11 w-full rounded-xl ${
              valueError ? "!border-red-300 focus:!border-red-400" : ""
            }`}
          />
          {valueError ? (
            <p className="mt-1.5 text-xs text-red-600" role="alert">
              {valueError}
            </p>
          ) : insufficientBalance ? (
            <p className="mt-1.5 text-xs text-amber-800" role="alert">
              {intl.get("pix.transfer.insufficientBalance", {
                available: sourceAccountBalance,
              })}
            </p>
          ) : (
            <p className="mt-1.5 text-xs text-gray-400">
              Utilize vírgula para centavos. Ex.: 125,50
            </p>
          )}
        </div>

        <div>
          <label
            className="mb-1.5 block text-sm font-medium text-gray-500"
            htmlFor="internal-transfer-description"
          >
            Descrição (opcional)
          </label>
          <input
            id="internal-transfer-description"
            name="description"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
            placeholder="Identifique o motivo da transferência"
            aria-label="Descrição da transferência"
            disabled={isSelfTransfer}
            className="valut-input h-11 w-full rounded-xl"
          />
        </div>

        {!hideFooter && (
          <div className="flex flex-col-reverse gap-2 border-t border-gray-100 pt-4 sm:flex-row sm:justify-end">
            <button
              type="submit"
              className="btn-primary"
              disabled={
                isSelfTransfer
                || sourceAccountBalanceLoading
                || sourceAccountBalanceError
                || sourceAccountBalanceCents == null
                || insufficientBalance
              }
            >
              Continuar
            </button>
          </div>
        )}
      </form>
    </section>
  );
};

export const InternalTransferConfirmStep = ({ contactData, linkValue, description }) => {
  const { title } = loadItem("Options");

  return (
    <section className="pix-transfer-section pix-transfer-section--compact app-card">
      <h3 className="pix-transfer-section__title">Confirmação</h3>
      <p className="pix-transfer-section__subtitle">
        Revise os dados antes de confirmar a transferência.
      </p>

      <div className="mt-2 space-y-2">
        <RecipientSummary contactData={contactData} title={title} />

        <div className="pix-transfer-recipient pix-transfer-recipient--compact">
          <div className="pix-transfer-recipient__row">
            <span className="pix-transfer-recipient__label">Valor</span>
            <span className="pix-transfer-recipient__value">R$ {linkValue}</span>
          </div>
          {description && (
            <div className="pix-transfer-recipient__row">
              <span className="pix-transfer-recipient__label">Descrição</span>
              <span className="pix-transfer-recipient__value">{description}</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
